import { Loader2, Plus, Upload, User, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";

const MAX_PHOTOS = 3;

async function processImageFile(file: File): Promise<string | null> {
  const needsConvert =
    /\.(heic|heif)$/i.test(file.name) ||
    file.type === "image/heic" ||
    file.type === "image/heif";

  let processFile: File | Blob = file;

  if (needsConvert) {
    try {
      const { heicTo } = await import("heic-to");
      const buf = await heicTo({ blob: file, type: "image/jpeg", quality: 0.85 });
      processFile = new Blob([buf], { type: "image/jpeg" });
    } catch {
      try {
        const heic2any = (await import("heic2any")).default;
        const conv = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.85 });
        processFile = Array.isArray(conv) ? conv[0] : conv;
      } catch {
        toast.error("HEIC conversion failed. Try JPEG or PNG.", { duration: 5000 });
        return null;
      }
    }
  }

  const bitmap = await createImageBitmap(processFile).catch(() => null);
  if (bitmap) {
    const canvas = document.createElement("canvas");
    const maxDim = 1536;
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d")!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.85);
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(processFile);
  });
}

interface PhotoUploadProps {
  label?: string;
  values: string[];
  onChange: (values: string[]) => void;
  onSelect?: () => void;
  onSuccess?: (photos: string[]) => void;
  onError?: (message: string) => void;
  hint?: string;
}

export function PhotoUpload({
  label,
  values,
  onChange,
  onSelect,
  onSuccess,
  onError,
  hint,
}: PhotoUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [converting, setConverting] = useState(false);
  // Use ref to always read latest values inside async handleFile
  const valuesRef = useRef<string[]>(values);
  valuesRef.current = values;

  const removePhoto = useCallback(
    (index: number) => {
      onChange(values.filter((_, i) => i !== index));
    },
    [values, onChange],
  );

  const handleFiles = async (files: File[]) => {
    const current = [...valuesRef.current];
    const results: string[] = [];

    for (const file of files) {
      if (current.length + results.length >= MAX_PHOTOS) break;

      const isImage =
        file.type.startsWith("image/") ||
        /\.(heic|heif|jpg|jpeg|png|webp)$/i.test(file.name);

      if (!isImage) continue;

      const b64 = await processImageFile(file);
      if (b64) results.push(b64);
    }

    if (results.length > 0) {
      onChange([...current, ...results]);
      onSuccess?.(results);
    } else {
      const formatError = "Please upload an image file (JPG, PNG, etc.)";
      toast.error(formatError);
      onError?.(formatError);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    onSelect?.();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files ?? []);

    await handleFiles(files);
  };

  const handleInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Reset input so the same file can be re-selected
    e.target.value = "";
    if (!files.length) return;

    await handleFiles(files);
  };

  if (converting) {
    return (
      <div className="space-y-2">
        {label && (
          <label className="block font-medium text-sm text-zinc-700">{label}</label>
        )}
        <div className="flex h-32 w-32 items-center justify-center rounded-2xl border-2 border-zinc-200 bg-zinc-50 sm:h-40 sm:w-40">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-xs text-zinc-400">Converting...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {label && (
        <label className="block font-medium text-sm text-zinc-700">{label}</label>
      )}

      {/* Uploaded photos row */}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {values.map((src, idx) => (
            <div key={idx} className="relative inline-block">
              <img
                src={src}
                alt={`Photo ${idx + 1}`}
                className="h-32 w-32 rounded-2xl border-2 border-zinc-200 object-cover shadow-sm sm:h-40 sm:w-40"
              />
              <button
                onClick={() => removePhoto(idx)}
                className="absolute -top-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-500 text-white shadow-md transition-transform hover:scale-110 active:scale-95"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}

          {/* Add more button */}
          {values.length < MAX_PHOTOS && (
            <label className="flex h-32 w-32 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-zinc-300 border-dashed bg-white transition-all hover:border-primary hover:bg-background sm:h-40 sm:w-40">
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleInput}
                onClick={onSelect}
              />
              <Plus className="h-6 w-6 text-zinc-400" />
              <span className="mt-1 text-xs text-zinc-400">
                Add photo ({values.length}/{MAX_PHOTOS})
              </span>
            </label>
          )}
        </div>
      )}

      {/* Initial dropzone — only shown when no photos uploaded yet */}
      {values.length === 0 && (
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 transition-all sm:p-8 ${
            isDragging
              ? "border-primary bg-background"
              : "border-zinc-300 bg-white hover:border-primary hover:bg-background"
          }`}
        >
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleInput}
            onClick={onSelect}
          />
          <div
            className={`mb-3 flex h-14 w-14 items-center justify-center rounded-2xl ${
              isDragging ? "bg-card" : "bg-zinc-100"
            }`}
          >
            {isDragging ? (
              <Upload className="h-6 w-6 text-primary" />
            ) : (
              <User className="h-6 w-6 text-zinc-400" />
            )}
          </div>
          <p className="font-medium text-sm text-zinc-600">
            Drop image here or <span className="text-primary">browse</span>
          </p>
          <p className="mt-0.5 text-xs text-zinc-400">Up to {MAX_PHOTOS} photos</p>
          {hint && <p className="mt-1 text-xs text-zinc-400">{hint}</p>}
        </label>
      )}
    </div>
  );
}
